import type { ChatModel } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';

/** endpoint 또는 bonding 의 id. kind 는 models 목록에서 역참조로 확인한다. */
const LS_TARGET_ID_KEY = 'innermate.chat.lastEndpointId'; // 이름은 호환성 위해 유지 (값은 endpoint or bonding id).
const LS_THINKING_KEY = 'innermate.chat.lastExtendedThinking';

function readLocal(): { targetId: string | null; extendedThinking: boolean } {
  try {
    return {
      targetId: window.localStorage.getItem(LS_TARGET_ID_KEY),
      extendedThinking: window.localStorage.getItem(LS_THINKING_KEY) === '1',
    };
  } catch {
    return { targetId: null, extendedThinking: false };
  }
}

function writeLocal(targetId: string | null, extendedThinking: boolean): void {
  try {
    if (targetId) window.localStorage.setItem(LS_TARGET_ID_KEY, targetId);
    else window.localStorage.removeItem(LS_TARGET_ID_KEY);
    window.localStorage.setItem(LS_THINKING_KEY, extendedThinking ? '1' : '0');
  } catch {
    // ignore
  }
}

export interface UseChatModel {
  /** 사용자에게 노출된 모델 목록 (endpoint + bonding 혼합). */
  models: ChatModel[];
  /** 현재 선택된 모델 (없으면 null — 로딩 중이거나 noop). */
  selected: ChatModel | null;
  /** 현재 적응형 사고 플래그. 본딩이면 항상 false. */
  extendedThinking: boolean;
  /** 로딩 여부. */
  isLoading: boolean;
  /** 선택 변경. 세션이 있으면 서버에 POST, 없으면 localStorage 에만 저장. */
  setSelected: (m: ChatModel) => void;
  /** 적응형 사고 토글. bonding 선택 시 무시. */
  setExtendedThinking: (v: boolean) => void;
}

interface Args {
  /** 기존 세션 편집 중일 때 세션 id + 저장된 값. 신규 챗이면 undefined. */
  session?: {
    id: string;
    llmEndpointId: string | null;
    llmBondingId: string | null;
    extendedThinking: boolean;
  };
}

/**
 * 세션이 있으면 세션 값, 없으면 localStorage → 시스템 기본값 순으로 선택 모델을 결정.
 * 변경은 세션이면 서버 POST, 아니면 localStorage 에만 기록.
 */
export function useChatModel({ session }: Args = {}): UseChatModel {
  const { data, isLoading } = useQuery({
    queryKey: ['chat', 'models'],
    queryFn: () => api.chatModels(),
    staleTime: 60_000,
  });
  const models = useMemo(() => data?.models ?? [], [data]);

  // 호출부에서 매 렌더 새 `session` 객체를 넘기기 때문에 primitive 필드로 쪼개서
  // 의존성 비교해야 useEffect 가 무한히 트리거되지 않는다.
  const sessionId = session?.id;
  const sessionEndpointId = session?.llmEndpointId ?? null;
  const sessionBondingId = session?.llmBondingId ?? null;
  const sessionExtendedThinking = session?.extendedThinking ?? false;
  const sessionTargetId = sessionBondingId ?? sessionEndpointId;

  // 로컬/세션 상태 초기화 — targetId 는 endpoint 또는 bonding id.
  const [localState, setLocalState] = useState<{
    targetId: string | null;
    extendedThinking: boolean;
  }>(() => {
    if (sessionId) {
      return {
        targetId: sessionTargetId,
        extendedThinking: sessionExtendedThinking,
      };
    }
    if (typeof window === 'undefined') {
      return { targetId: null, extendedThinking: false };
    }
    return readLocal();
  });

  // 세션이 바뀌면 상태 재동기화. 단, 사용자가 방금 고른 유효한 모델을
  // 서버 응답 (낡은 값 또는 첫 메시지 처리 전 null) 로 덮어써서 picker 가
  // 깜빡이지 않도록 가드한다.
  useEffect(() => {
    if (!sessionId) return;
    setLocalState((prev) => {
      const prevValid = prev.targetId ? models.some((m) => m.id === prev.targetId) : false;
      // 신규 세션의 첫 fetch 등으로 서버 모델이 비어 있으면 사용자 선택 유지.
      if (prevValid && sessionTargetId === null) return prev;
      // 서버 값과 이미 일치하면 noop (참조 안정성 유지).
      if (prev.targetId === sessionTargetId && prev.extendedThinking === sessionExtendedThinking) {
        return prev;
      }
      return {
        targetId: sessionTargetId,
        extendedThinking: sessionExtendedThinking,
      };
    });
  }, [sessionId, sessionTargetId, sessionExtendedThinking, models]);

  const selected = useMemo(() => {
    if (models.length === 0) return null;
    const byId = localState.targetId ? models.find((m) => m.id === localState.targetId) : undefined;
    if (byId) return byId;
    const def = models.find((m) => m.isDefault);
    return def ?? models[0] ?? null;
  }, [models, localState.targetId]);

  const setSelected = useCallback(
    (m: ChatModel) => {
      // bonding 또는 미지원 endpoint 면 thinking 강제로 꺼둠.
      const nextThinking = m.supportsExtendedThinking ? localState.extendedThinking : false;
      setLocalState({ targetId: m.id, extendedThinking: nextThinking });
      if (sessionId) {
        void api
          .chatSetSessionModel(
            sessionId,
            m.kind === 'bonding'
              ? { bondingId: m.id, extendedThinking: nextThinking }
              : { endpointId: m.id, extendedThinking: nextThinking },
          )
          .catch(() => undefined);
      } else {
        writeLocal(m.id, nextThinking);
      }
    },
    [sessionId, localState.extendedThinking],
  );

  const setExtendedThinking = useCallback(
    (v: boolean) => {
      // 현재 선택된 모델이 thinking 지원하지 않으면 noop.
      if (selected && !selected.supportsExtendedThinking && v) return;
      setLocalState((s) => ({ ...s, extendedThinking: v }));
      if (sessionId) {
        void api.chatSetSessionModel(sessionId, { extendedThinking: v }).catch(() => undefined);
      } else {
        writeLocal(localState.targetId, v);
      }
    },
    [sessionId, selected, localState.targetId],
  );

  return {
    models,
    selected,
    extendedThinking: localState.extendedThinking,
    isLoading,
    setSelected,
    setExtendedThinking,
  };
}
